import { useCallback, useEffect, useState } from 'react';
import {
  sopPlanningApi,
  type ActionItem,
  type ConsensusRecord,
  type ExecutiveDecision,
  type MeetingRecord,
  type PlanAlignment,
  type PlanningException,
  type SOPCycle,
  type SOPDashboard,
  type SOPRecommendation,
} from '../../api/sopplanning';

type Tab = 'dashboard' | 'consensus' | 'exceptions' | 'decisions' | 'meetings' | 'alignments' | 'recommendations' | 'actions';
type Theme = 'light' | 'dark';

function fmtMoney(n: number) {
  if (n >= 1_000_000) return `$${(n / 1_000_000).toFixed(2)}M`;
  if (n >= 1_000) return `$${(n / 1_000).toFixed(1)}K`;
  return `$${n.toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
}

function severityColor(severity: string, dark: boolean) {
  if (severity === 'high') return dark ? 'text-red-400' : 'text-red-600';
  if (severity === 'moderate') return dark ? 'text-amber-400' : 'text-amber-600';
  return dark ? 'text-slate-400' : 'text-slate-600';
}

export function SOPPlanningPage() {
  const [theme, setTheme] = useState<Theme>('light');
  const dark = theme === 'dark';
  const [tab, setTab] = useState<Tab>('dashboard');
  const [dashboard, setDashboard] = useState<SOPDashboard | null>(null);
  const [cycle, setCycle] = useState<SOPCycle | null>(null);
  const [consensus, setConsensus] = useState<ConsensusRecord[]>([]);
  const [exceptions, setExceptions] = useState<PlanningException[]>([]);
  const [decisions, setDecisions] = useState<ExecutiveDecision[]>([]);
  const [meetings, setMeetings] = useState<MeetingRecord[]>([]);
  const [actions, setActions] = useState<ActionItem[]>([]);
  const [alignments, setAlignments] = useState<PlanAlignment[]>([]);
  const [recommendations, setRecommendations] = useState<SOPRecommendation[]>([]);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    setError(null);
    try {
      const [dash, cycles] = await Promise.all([sopPlanningApi.dashboard(), sopPlanningApi.listCycles()]);
      setDashboard(dash);
      const c = cycles[0] ?? null;
      setCycle(c);
      if (!c) return;
      const [cons, exc, dec, mtg, act, align, recs] = await Promise.all([
        sopPlanningApi.listConsensus(c.id),
        sopPlanningApi.listExceptions(c.id),
        sopPlanningApi.listDecisions(c.id),
        sopPlanningApi.listMeetings(c.id),
        sopPlanningApi.listActions(c.id),
        sopPlanningApi.listAlignments(c.id),
        sopPlanningApi.listRecommendations(c.id),
      ]);
      setConsensus(cons);
      setExceptions(exc);
      setDecisions(dec);
      setMeetings(mtg);
      setActions(act);
      setAlignments(align);
      setRecommendations(recs);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load S&OP planning');
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const shell = dark ? 'min-h-screen bg-slate-950 text-slate-100' : 'min-h-screen bg-slate-50 text-slate-900';
  const card = dark ? 'rounded-xl border border-slate-800 bg-slate-900 p-5' : 'rounded-xl border border-slate-200 bg-white p-5 shadow-sm';
  const header = dark ? 'border-b border-slate-800 bg-slate-900' : 'border-b border-slate-200 bg-white';
  const muted = dark ? 'text-slate-400' : 'text-slate-600';

  const tabs: { id: Tab; label: string }[] = [
    { id: 'dashboard', label: 'Command Center' },
    { id: 'consensus', label: 'Consensus' },
    { id: 'exceptions', label: 'Exceptions' },
    { id: 'alignments', label: 'Plan Alignment' },
    { id: 'decisions', label: 'Decision Register' },
    { id: 'meetings', label: 'Meetings' },
    { id: 'recommendations', label: 'AI S&OP Advisor' },
    { id: 'actions', label: 'Action Tracker' },
  ];

  return (
    <div className={shell}>
      <div className={`${header} px-6 py-4`}>
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-semibold">Enterprise S&OP Platform</h1>
            <p className={`mt-1 text-sm ${muted}`}>Integrated business planning governance, consensus planning, executive review, and AI S&OP intelligence</p>
          </div>
          <div className="flex gap-2">
            <button type="button" onClick={() => setTheme(dark ? 'light' : 'dark')} className={`rounded-lg px-3 py-2 text-sm ${dark ? 'bg-slate-800' : 'bg-slate-100'}`} aria-label="Toggle dark mode">{dark ? 'Light' : 'Dark'}</button>
            <button type="button" onClick={refresh} className="rounded-lg bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700">Refresh</button>
          </div>
        </div>
        {cycle && (
          <p className={`mt-2 text-sm ${muted}`}>
            {cycle.name} · {cycle.period} · Alignment {cycle.alignment_score.toFixed(0)}% · Readiness {cycle.enterprise_readiness_score.toFixed(0)}
          </p>
        )}
      </div>
      {error && <div className="mx-6 mt-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700 dark:border-red-900 dark:bg-red-950 dark:text-red-300" role="alert">{error}</div>}
      <div className={`${header} px-6`}>
        <nav className="flex gap-1 overflow-x-auto" role="tablist">
          {tabs.map((t) => (
            <button key={t.id} type="button" role="tab" aria-selected={tab === t.id} onClick={() => setTab(t.id)}
              className={`whitespace-nowrap border-b-2 px-4 py-3 text-sm font-medium ${tab === t.id ? 'border-indigo-600 text-indigo-600' : 'border-transparent text-slate-500'}`}>{t.label}</button>
          ))}
        </nav>
      </div>
      <div className="p-6">
        {tab === 'dashboard' && dashboard && (
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {[
              { label: 'Alignment Score', value: `${dashboard.alignment_score.toFixed(0)}%` },
              { label: 'Consensus Score', value: `${dashboard.consensus_score.toFixed(0)}%` },
              { label: 'Enterprise Readiness', value: dashboard.enterprise_readiness_score.toFixed(0) },
              { label: 'Planning Maturity', value: dashboard.planning_maturity_score.toFixed(0) },
              { label: 'Consensus Rate', value: `${dashboard.consensus_rate.toFixed(0)}%` },
              { label: 'Service Level', value: `${dashboard.service_level.toFixed(1)}%` },
              { label: 'Revenue Achievement', value: `${dashboard.revenue_achievement.toFixed(0)}%` },
              { label: 'Supply Reliability', value: `${dashboard.supply_reliability.toFixed(0)}%` },
              { label: 'Decision Cycle Time', value: `${dashboard.decision_cycle_time_days.toFixed(1)} days` },
              { label: 'Open Exceptions', value: dashboard.open_exceptions },
              { label: 'Open Conflicts', value: dashboard.open_conflicts },
              { label: 'Pending Recommendations', value: dashboard.pending_recommendations },
            ].map((m) => (
              <div key={m.label} className={card}><p className={`text-sm ${muted}`}>{m.label}</p><p className="mt-1 text-2xl font-semibold">{m.value}</p></div>
            ))}
          </div>
        )}
        {tab === 'consensus' && consensus.map((c) => (
          <div key={c.id} className={`${card} mb-4`}>
            <div className="flex items-center justify-between">
              <h3 className="font-medium">{c.function_area}</h3>
              <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${c.status === 'achieved' ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900 dark:text-emerald-300' : 'bg-amber-100 text-amber-700 dark:bg-amber-900 dark:text-amber-300'}`}>{c.status}</span>
            </div>
            <p className={`mt-1 text-sm ${muted}`}>Reviewer: {c.reviewer ?? 'Pending'} · Conflicts {c.resolved_count}/{c.conflict_count} resolved</p>
            {c.assumptions && <p className={`mt-2 text-sm ${muted}`}>Assumptions: {c.assumptions}</p>}
          </div>
        ))}
        {tab === 'exceptions' && exceptions.map((e) => (
          <div key={e.id} className={`${card} mb-4`}>
            <div className="flex items-center justify-between">
              <h3 className="font-medium">{e.exception_type.replace(/_/g, ' ')}</h3>
              <span className={`text-sm font-medium ${severityColor(e.severity, dark)}`}>{e.severity}</span>
            </div>
            <p className={`mt-1 text-sm ${muted}`}>{e.source_domain} → {e.target_domain}</p>
            <p className="mt-2 text-sm">{e.business_impact}</p>
            <p className={`mt-1 text-sm ${muted}`}>Root cause: {e.root_cause}</p>
            <p className="mt-1 text-sm text-indigo-600 dark:text-indigo-400">Resolution: {e.recommended_resolution}</p>
          </div>
        ))}
        {tab === 'alignments' && alignments.map((a) => (
          <div key={a.id} className={`${card} mb-4`}>
            <h3 className="font-medium">{a.source_domain} ↔ {a.target_domain}</h3>
            <div className="mt-2 h-2 rounded-full bg-slate-200 dark:bg-slate-700">
              <div className={`h-2 rounded-full ${a.alignment_pct >= 85 ? 'bg-emerald-600' : 'bg-amber-500'}`} style={{ width: `${Math.min(a.alignment_pct, 100)}%` }} />
            </div>
            <p className={`mt-1 text-sm ${muted}`}>{a.alignment_pct.toFixed(0)}% aligned · {a.gap_description}</p>
            {a.recommended_action && <p className="mt-1 text-sm text-indigo-600 dark:text-indigo-400">{a.recommended_action}</p>}
          </div>
        ))}
        {tab === 'decisions' && decisions.map((d) => (
          <div key={d.id} className={`${card} mb-4`}>
            <div className="flex items-center justify-between">
              <h3 className="font-medium">{d.title}</h3>
              <span className={`rounded-full px-2 py-0.5 text-xs ${d.approval_status === 'approved' ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}`}>{d.approval_status}</span>
            </div>
            <p className={`mt-1 text-sm ${muted}`}>Owner: {d.decision_owner} · Impact {fmtMoney(d.financial_impact)}</p>
            <p className="mt-2 text-sm">{d.business_justification}</p>
          </div>
        ))}
        {tab === 'meetings' && meetings.map((m) => (
          <div key={m.id} className={`${card} mb-4`}>
            <div className="flex items-center justify-between">
              <h3 className="font-medium">{m.title}</h3>
              <span className={`text-xs ${muted}`}>{m.status}</span>
            </div>
            <p className={`mt-1 text-sm ${muted}`}>{m.meeting_type.replace(/_/g, ' ')} · {new Date(m.scheduled_at).toLocaleDateString()}</p>
            <p className={`mt-1 text-sm ${muted}`}>Attendees: {m.attendees.join(', ')}</p>
            {m.agenda && <p className="mt-2 text-sm">{m.agenda}</p>}
            {m.minutes && <p className={`mt-1 text-sm italic ${muted}`}>{m.minutes}</p>}
          </div>
        ))}
        {tab === 'recommendations' && recommendations.map((r) => (
          <div key={r.id} className={`${card} mb-4`}>
            <h3 className="font-medium">{r.title}</h3>
            <p className={`mt-1 text-sm ${muted}`}>{r.type.replace(/_/g, ' ')} · Confidence {(r.confidence * 100).toFixed(0)}%</p>
            <p className="mt-2 text-sm">{r.business_explanation}</p>
            <p className={`mt-1 text-sm ${muted}`}>Outcome: {r.expected_business_outcome} · Financial impact {fmtMoney(r.expected_financial_impact)}</p>
          </div>
        ))}
        {tab === 'actions' && actions.map((a) => (
          <div key={a.id} className={`${card} mb-4 flex items-center justify-between`}>
            <div>
              <h3 className="font-medium">{a.title}</h3>
              <p className={`mt-1 text-sm ${muted}`}>{a.owner} · Due {a.due_date ?? 'TBD'}</p>
            </div>
            <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${a.priority === 'high' ? 'bg-red-100 text-red-700 dark:bg-red-900 dark:text-red-300' : 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300'}`}>{a.status}</span>
          </div>
        ))}
      </div>
    </div>
  );
}
